export const PROFILE_CACHE_TTL = 5 * 60 * 1000;

const profileCache = new Map();
const activeProfileRequests = new Map();
let profileCacheGeneration = 0;

export const getProfileCacheKey = (uid) =>
  uid ? `profile:${String(uid).trim()}` : '';

export const getCachedProfile = (uid) => {
  const key = getProfileCacheKey(uid);
  return key ? profileCache.get(key)?.data : undefined;
};

export const setCachedProfile = (uid, profile) => {
  const key = getProfileCacheKey(uid);

  if (key) {
    profileCache.set(key, {
      data: profile,
      timestamp: Date.now(),
    });
  }

  return profile;
};

export const loadProfileWithCache = (uid, request) => {
  const key = getProfileCacheKey(uid);

  if (!key) {
    return Promise.reject(new Error('PROFILE_UID_REQUIRED'));
  }

  const entry = profileCache.get(key);
  const isFresh =
    entry && Date.now() - entry.timestamp < PROFILE_CACHE_TTL;

  if (isFresh) {
    return Promise.resolve(entry.data);
  }

  const activeRequest = activeProfileRequests.get(key);

  if (activeRequest) {
    return activeRequest;
  }

  const requestGeneration = profileCacheGeneration;
  const requestPromise = Promise.resolve()
    .then(request)
    .then((profile) => {
      if (requestGeneration === profileCacheGeneration) {
        setCachedProfile(uid, profile);
      }

      return profile;
    })
    .finally(() => {
      if (activeProfileRequests.get(key) === requestPromise) {
        activeProfileRequests.delete(key);
      }
    });

  activeProfileRequests.set(key, requestPromise);
  return requestPromise;
};

export const invalidateProfileCache = (uid) => {
  if (!uid) {
    profileCacheGeneration += 1;
    profileCache.clear();
    activeProfileRequests.clear();
    return;
  }

  profileCache.delete(getProfileCacheKey(uid));
};

export const resetProfileCacheForTests = () => {
  profileCacheGeneration += 1;
  profileCache.clear();
  activeProfileRequests.clear();
};
